// receiptPrinter.js

const receiptPrinter = {
    /**
     * Print a receipt with convenience store style format
     * @param {Object} data - Receipt data
     * @param {Object} options - Printing options
     */
    printReceipt(data, options = {}) {
        const receiptHTML = this.generateReceiptHTML(data);
        this.printIframe(receiptHTML, options);
    },

    /**
     * Generate receipt HTML with store receipt format
     */
    generateReceiptHTML(data) {
        const {
            storeName = 'YOUR STORE NAME',
            storeAddress = '123 Main Street, City',
            storePhone = 'Tel: (123) 456-7890',
            receiptNumber = this.generateReceiptNumber(),
            cashier = 'CASHIER',
            date = new Date().toLocaleString(),
            items = [],
            subtotal = 0,
            tax = 0,
            discount = 0,
            total = 0,
            payment = 0,
            change = 0,
            paymentMethod = 'CASH',
            customerName = '',
            customerEmail = ''
        } = data;

        // Calculate totals if not provided
        const calculatedSubtotal = subtotal || items.reduce((sum, item) => sum + (item.price * item.qty), 0);
        const calculatedTotal = total || (calculatedSubtotal + tax - discount);
        const calculatedChange = change || (payment - calculatedTotal);

        // Generate items HTML
        const itemsHTML = items.map(item => `
            <tr>
                <td class="item-qty">${item.qty}</td>
                <td class="item-name">${item.name}</td>
                <td class="item-price">${this.formatCurrency(item.price)}</td>
                <td class="item-total">${this.formatCurrency(item.price * item.qty)}</td>
            </tr>
        `).join('');

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Receipt #${receiptNumber}</title>
                <style>
                    @page {
                        size: 80mm auto; /* Standard thermal receipt width */
                        margin: 0;
                    }
                    
                    body {
                        font-family: 'Courier New', monospace;
                        font-size: 12px;
                        width: 72mm; /* Slightly less than page size for margins */
                        margin: 0 auto;
                        padding: 2mm;
                        background: white;
                        color: black;
                    }
                    
                    .receipt {
                        max-width: 100%;
                    }
                    
                    /* Header */
                    .store-header {
                        text-align: center;
                        margin-bottom: 10px;
                        padding-bottom: 5px;
                        border-bottom: 1px dashed #000;
                    }
                    
                    .store-name {
                        font-size: 18px;
                        font-weight: bold;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        margin: 5px 0;
                    }
                    
                    .store-info {
                        font-size: 10px;
                        line-height: 1.2;
                        margin: 3px 0;
                    }
                    
                    /* Receipt info */
                    .receipt-info {
                        margin: 10px 0;
                        padding: 5px 0;
                        border-bottom: 1px dashed #000;
                        font-size: 11px;
                    }
                    
                    .info-row {
                        display: flex;
                        justify-content: space-between;
                        margin: 2px 0;
                    }
                    
                    /* Items table */
                    .items-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 10px 0;
                        font-size: 11px;
                    }
                    
                    .items-table th {
                        text-align: left;
                        border-bottom: 1px solid #000;
                        padding: 3px 0;
                        font-weight: bold;
                    }
                    
                    .items-table td {
                        padding: 2px 0;
                    }
                    
                    .item-qty {
                        width: 10%;
                        text-align: right;
                        padding-right: 5px;
                    }
                    
                    .item-name {
                        width: 50%;
                    }
                    
                    .item-price {
                        width: 20%;
                        text-align: right;
                        padding-right: 5px;
                    }
                    
                    .item-total {
                        width: 20%;
                        text-align: right;
                    }
                    
                    /* Summary section */
                    .summary {
                        margin: 10px 0;
                        padding: 5px 0;
                        border-top: 1px dashed #000;
                        border-bottom: 1px dashed #000;
                    }
                    
                    .summary-row {
                        display: flex;
                        justify-content: space-between;
                        margin: 3px 0;
                        font-size: 11px;
                    }
                    
                    .summary-row.total {
                        font-size: 14px;
                        font-weight: bold;
                        border-top: 1px solid #000;
                        border-bottom: 1px solid #000;
                        padding: 5px 0;
                        margin: 5px 0;
                    }
                    
                    /* Payment section */
                    .payment-section {
                        margin: 10px 0;
                        padding: 5px 0;
                    }
                    
                    /* Footer */
                    .receipt-footer {
                        text-align: center;
                        margin-top: 15px;
                        padding-top: 5px;
                        border-top: 1px dashed #000;
                        font-size: 10px;
                    }
                    
                    .thank-you {
                        font-size: 14px;
                        font-weight: bold;
                        margin: 5px 0;
                        text-transform: uppercase;
                    }
                    
                    .barcode {
                        font-family: 'Libre Barcode 39', 'Courier New', monospace;
                        font-size: 24px;
                        margin: 10px 0;
                        letter-spacing: 2px;
                    }
                    
                    /* Cut line */
                    .cut-line {
                        text-align: center;
                        margin: 15px 0 5px 0;
                        font-size: 12px;
                    }
                    
                    .cut-line:before,
                    .cut-line:after {
                        content: '---';
                        margin: 0 5px;
                    }
                    
                    /* Laser/thermal specific */
                    .laser-mark {
                        text-align: center;
                        font-size: 8px;
                        margin: 2px 0;
                        color: #666;
                    }
                    
                    @media print {
                        body {
                            width: 72mm;
                            margin: 0;
                            padding: 2mm;
                        }
                        
                        .no-print {
                            display: none;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="receipt">
                    <!-- Store Header -->
                    <div class="store-header">
                        <div class="store-name">${storeName}</div>
                        <div class="store-info">${storeAddress}</div>
                        <div class="store-info">${storePhone}</div>
                        <div class="laser-mark">✦ LASER COPY ✦</div>
                    </div>

                    <!-- Receipt Info -->
                    <div class="receipt-info">
                        <div class="info-row">
                            <span>RECEIPT #:</span>
                            <span>${receiptNumber}</span>
                        </div>
                        <div class="info-row">
                            <span>DATE/TIME:</span>
                            <span>${date}</span>
                        </div>
                        <div class="info-row">
                            <span>CASHIER:</span>
                            <span>${cashier}</span>
                        </div>
                        ${customerName ? `
                        <div class="info-row">
                            <span>CUSTOMER:</span>
                            <span>${customerName}</span>
                        </div>
                        ` : ''}
                        ${customerEmail ? `
                        <div class="info-row">
                            <span>EMAIL:</span>
                            <span>${customerEmail}</span>
                        </div>
                        ` : ''}
                    </div>

                    <!-- Items -->
                    <table class="items-table">
                        <thead>
                            <tr>
                                <th>Qty</th>
                                <th>Item</th>
                                <th>Price</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${itemsHTML}
                        </tbody>
                    </table>

                    <!-- Summary -->
                    <div class="summary">
                        <div class="summary-row">
                            <span>SUBTOTAL:</span>
                            <span>${this.formatCurrency(calculatedSubtotal)}</span>
                        </div>
                        ${discount > 0 ? `
                        <div class="summary-row">
                            <span>DISCOUNT:</span>
                            <span>-${this.formatCurrency(discount)}</span>
                        </div>
                        ` : ''}
                        <div class="summary-row">
                            <span>TAX (${data.taxRate || 0}%):</span>
                            <span>${this.formatCurrency(tax)}</span>
                        </div>
                        <div class="summary-row total">
                            <span>TOTAL:</span>
                            <span>${this.formatCurrency(calculatedTotal)}</span>
                        </div>
                    </div>

                    <!-- Payment -->
                    <div class="payment-section">
                        <div class="info-row">
                            <span>PAYMENT METHOD:</span>
                            <span>${paymentMethod}</span>
                        </div>
                        <div class="info-row">
                            <span>AMOUNT PAID:</span>
                            <span>${this.formatCurrency(payment)}</span>
                        </div>
                        <div class="info-row">
                            <span>CHANGE:</span>
                            <span>${this.formatCurrency(calculatedChange)}</span>
                        </div>
                    </div>

                    <!-- Footer -->
                    <div class="receipt-footer">
                        <div class="thank-you">THANK YOU FOR SHOPPING!</div>
                        <div>PLEASE COME AGAIN</div>
                        <div class="barcode">*${receiptNumber.slice(-6)}*</div>
                        <div>${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
                        <div class="cut-line"></div>
                        <div class="laser-mark">LASER PRINTED RECEIPT • VALID WITH STORE STAMP</div>
                    </div>

                    <!-- Print button (hidden when printing) -->
                    <div class="no-print" style="text-align: center; margin-top: 20px;">
                        <button onclick="window.print()" style="padding: 10px 20px; background: #4CAF50; color: white; border: none; border-radius: 5px; cursor: pointer;">
                            Print Receipt
                        </button>
                        <button onclick="window.close()" style="padding: 10px 20px; background: #f44336; color: white; border: none; border-radius: 5px; cursor: pointer; margin-left: 10px;">
                            Close
                        </button>
                    </div>
                </div>

                <script>
                    // Auto-trigger print when loaded (optional)
                    window.onload = function() {
                        // Uncomment to auto-print
                        // window.print();
                    };
                </script>
            </body>
            </html>
        `;
    },

    /**
     * Print HTML content using an iframe
     */
    printIframe(htmlContent, options = {}) {
        const {
            autoPrint = true,
            showDialog = true
        } = options;

        // Create iframe
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.style.visibility = 'hidden';
        
        document.body.appendChild(iframe);

        // Write content to iframe
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();

        // Handle print after content loads
        iframe.onload = () => {
            if (autoPrint) {
                setTimeout(() => {
                    if (showDialog) {
                        iframe.contentWindow.print();
                    } else {
                        iframe.contentWindow.print(); // Most browsers always show dialog
                    }
                    
                    // Optional: remove iframe after printing
                    setTimeout(() => {
                        document.body.removeChild(iframe);
                    }, 1000);
                }, 100);
            }
        };
    },

    /**
     * Generate unique receipt number
     */
    generateReceiptNumber() {
        const date = new Date();
        const year = date.getFullYear().toString().slice(-2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        return `${year}${month}${day}-${random}`;
    },

    /**
     * Format currency
     */
    formatCurrency(amount) {
        return new Intl.NumberFormat('en-PH', {
            style: 'currency',
            currency: 'PHP',
            minimumFractionDigits: 2
        }).format(amount).replace('PHP', '₱');
    },

    /**
     * Direct print method without iframe (uses popup)
     */
    printPopup(data) {
        const receiptHTML = this.generateReceiptHTML(data);
        const printWindow = window.open('', '_blank', 'width=400,height=600');
        
        printWindow.document.write(receiptHTML);
        printWindow.document.close();
        
        printWindow.focus();
        printWindow.print();
        
        // Close after print (optional)
        printWindow.onafterprint = () => {
            printWindow.close();
        };
    },

    /**
     * Example receipt data for testing
     */
    getExampleData() {
        return {
            storeName: '7-ELEVEN',
            storeAddress: '123 Main Street, Downtown',
            storePhone: 'Tel: (02) 8123-4567',
            cashier: 'MARIA S.',
            items: [
                { qty: 2, name: 'Mineral Water 500ml', price: 25.00 },
                { qty: 1, name: 'Sandwich - Tuna', price: 55.00 },
                { qty: 3, name: 'Gummy Bears', price: 10.50 },
                { qty: 1, name: 'Cigarettes - Marlboro', price: 125.00 },
                { qty: 2, name: 'Batteries AA', price: 45.00 }
            ],
            subtotal: 338.00,
            tax: 20.28,
            taxRate: 6,
            discount: 0,
            total: 358.28,
            payment: 400.00,
            change: 41.72,
            paymentMethod: 'CASH',
            customerName: 'JUAN DELA CRUZ',
            receiptNumber: this.generateReceiptNumber()
        };
    }
};

export default receiptPrinter;